import { GetStaticProps } from "next";
import {
  Community,
  CommunityEvents,
  OrgBootstrap,
} from "@dreambigger/shared/src/types";
import { generateWeserveQueryParams } from "@dreambigger/shared/src/utils/query-params";
import {
  communityApi,
  orgApi,
} from "@dreambigger/shared/src/api/weserve/api-helper";
import { useTryAsync } from "no-try";
import { CommunitySlugParams } from "../../utils/static-paths-helper";
import BoardWrapper from "../boards/board-wrapper";
import { is404 } from "@dreambigger/shared/src/utils";

/**
 * Pre-render the page with the following data from the Weserve API.
 *  - Community: Response from GET /communities (General Info to use throughout the page, i.e Community Meta)
 *  - CommunityEvents: Response from GET /organizations/:id/events (The Fundraiser/Event story Cards on the Page)
 *  - OrgBootstrap: Response from GET /organizations/:id/bootstrap (Footer and Header)
 */
type CommunityEventsProps = {
  community: Community;
  communityEvents: CommunityEvents;
  orgBootstrap: OrgBootstrap;
};

/**
 * Default Event Pagination Options
 * Sorting events by ascending "start_at" time and limiting to 25 records per api call.
 */
const DEFAULT_SORT = "start_at";
const DEFAULT_SORT_DIRECTION = "asc";
const DEFAULT_PAGINATION_LENGTH = 25;
const DEFAULT_STATIC_REVALIDATION_SECONDS = 120;

// Community Events Page Component
const CommunityEventsPage = (props: CommunityEventsProps) => {
  return (
    <BoardWrapper
      initialData={[props.communityEvents]}
      community={props.community}
      orgBootstrap={props.orgBootstrap}
      filterDefaults={{
        sort: DEFAULT_SORT,
        direction: DEFAULT_SORT_DIRECTION,
        pagination: DEFAULT_PAGINATION_LENGTH,
      }}
      boardType="events"
    />
  );
};

// Get data to statically generate the page for each community.
export const getCommunityEventsStaticProps: GetStaticProps<
  CommunityEventsProps,
  CommunitySlugParams
> = async (context) => {
  const { communitySlug } = context.params!;

  // Get the community. If it doesn't exist return a 404 page.
  const [error, communityResponse] = await useTryAsync(() =>
    communityApi.getBySlug(communitySlug)
  );

  if (error && is404(error)) {
    console.error("Unable to find community:", error);
    return {
      notFound: true,
    };
  }

  // throw error to ensure statically generated page is not replaced from CDN during ISR
  if (error) {
    throw error;
  }
  // Add a null check before accessing communityResponse.data
  if (!communityResponse || !communityResponse.data) {
    throw new Error("Community response is null or undefined");
  }

  const community = communityResponse.data;

  // The amount in seconds we want to rehydrate static pages with new content.
  const regenerationTime =
    community.meta?.options?.staticGeneration?.revalidate ||
    DEFAULT_STATIC_REVALIDATION_SECONDS;

  const organizationId = community.organization.id;

  // Get the organization's bootstrap data.
  const orgBootstrapResponse = await orgApi.getOrgBootstrap(organizationId);
  const orgBootstrap = orgBootstrapResponse.data;

  // Get the community's events.
  const paginationLength =
    community?.meta?.feeds?.paginationLength || DEFAULT_PAGINATION_LENGTH;

  const eventsResponse = await orgApi.getEvents(
    organizationId,
    generateWeserveQueryParams({
      offset: "0",
      limit: String(paginationLength),
      sort: DEFAULT_SORT,
      direction: DEFAULT_SORT_DIRECTION,
      endAtGte: new Date().toISOString(),
    })
  );

  const communityEvents = eventsResponse.data;

  /**
   * The events + data we want to pre-render for each community.
   * Next.js will attempt to regenerate the page at most every "n" seconds
   * based on the value passed to revalidate when a request is made to this page.
   */
  return {
    props: {
      community,
      communityEvents,
      orgBootstrap,
    },
    revalidate: regenerationTime,
  };
};

export default CommunityEventsPage;
